/**
 * Central Permission Configuration (Frontend)
 * 
 * This file should be kept in sync with backend config/permissionsConfig.js
 * Defines all permissions, their dependencies, and parent-child relationships.
 */

// All available permissions in the system
export const ALL_PERMISSIONS = [
  // Clients
  'view_clients',
  'edit_clients',
  'edit_client_details', // Backward compatibility alias for edit_clients
  'add_clients',
  'renew_clients',
  'change_client_password',
  'view_client_details',
  'view_client_agent',
  'view_client_payment',
  'edit_client_payment',
  'view_payments',
  'transfer_client_to_agent',
  
  // Leads
  'view_leads',
  'create_leads',
  'edit_leads',
  
  // Tasks & Managers
  'view_tasks',
  'create_tasks',
  'edit_tasks',
  'delete_tasks',
  'complete_tasks',
  'view_manager',
  'create_manager',
  'edit_manager',
  'delete_manager',
  'change_manager_password',
  
  // Agents
  'create_agent',
  'view_agent',
  'edit_agent',
  'change_agent_password',
  'delete_agent',
  
  // Potential Clients
  'view_potential_clients',
  'edit_potential_clients',
  'delete_potential_clients',
  'convert_potential_clients',
  
  // System
  'view_slots',
  'create_slots',
  'edit_slots',
  'overview',
  
  // Management & Advanced Permissions
  'manage_orders',
  'manage_clients',
  'manage_task_kinds',
  'assign_leads',
  'manage_leads',
  'manage_api_keys',
  'delete_clients',
  'view_sessions',
  'view_failed_logins',
  'unlock_accounts',
  
  // Proposals (Price Quotes)
  'view_proposals',
  'create_proposals',
  'edit_proposals',
  // Vendors (Suppliers)
  'view_vendors',
  'edit_vendors',
] as const;

// Permissions that REQUIRE other permissions to be ON
// When a permission is enabled, its dependencies are automatically added
export const PERMISSION_DEPENDENCIES: Record<string, string[]> = {
  // Clients dependencies
  edit_clients: ['view_clients', 'view_client_details'],
  add_clients: ['view_clients'],
  renew_clients: ['view_clients'],
  change_client_password: ['view_clients', 'view_client_details'],
  view_client_details: ['view_clients'],
  view_client_agent: ['view_clients'],
  view_client_payment: ['view_clients', 'view_client_details'],
  edit_client_payment: ['view_clients', 'view_client_payment', 'view_payments'],
  transfer_client_to_agent: ['view_clients', 'view_client_details'],
  
  // Leads dependencies
  create_leads: ['view_leads'],
  edit_leads: ['view_leads'],
  
  // Tasks dependencies
  create_tasks: ['view_tasks'],
  edit_tasks: ['view_tasks'],
  delete_tasks: ['view_tasks'],
  complete_tasks: ['view_tasks'],
  
  // Manager dependencies
  create_manager: ['view_manager'],
  edit_manager: ['view_manager'],
  delete_manager: ['view_manager'],
  change_manager_password: ['view_manager'],
  
  // Agent dependencies
  create_agent: ['view_agent'],
  edit_agent: ['view_agent'],
  delete_agent: ['view_agent'],
  change_agent_password: ['view_agent'],
  
  // Potential clients dependencies
  edit_potential_clients: ['view_potential_clients'],
  delete_potential_clients: ['view_potential_clients'],
  convert_potential_clients: ['view_potential_clients'],
  
  // Slots dependencies
  create_slots: ['view_slots'],
  edit_slots: ['view_slots'],
  
  // Proposals dependencies
  create_proposals: ['view_proposals'],
  edit_proposals: ['view_proposals'],
  
  // Vendors
  view_vendors: [],
  edit_vendors: ['view_vendors'],
  
  // Permissions with no dependencies (base permissions)
  view_clients: [],
  view_payments: [],
  view_leads: [],
  view_tasks: [],
  view_manager: [],
  view_agent: [],
  view_potential_clients: [],
  view_slots: [],
  view_proposals: [],
  overview: [],
  
  // Management & Advanced Permissions dependencies
  manage_orders: ['view_clients'], // Orders are related to clients
  manage_clients: ['view_clients'],
  manage_task_kinds: ['view_tasks'],
  assign_leads: ['view_leads'],
  manage_leads: ['view_leads'],
  manage_api_keys: [], // API keys management is independent
  delete_clients: ['view_clients'],
  view_sessions: [], // Read-only permission
  view_failed_logins: [], // Read-only permission
  unlock_accounts: ['view_sessions'], // Unlocking requires viewing sessions
};

// Derived: when turning OFF one permission, we should also turn OFF its children
// This ensures logical consistency (e.g., if you can't view clients, you can't edit them)
export const PERMISSION_CHILDREN: Record<string, string[]> = {
  view_clients: [
    'edit_clients',
    'add_clients',
    'renew_clients',
    'change_client_password',
    'view_client_details',
    'view_client_agent',
    'view_client_payment',
    'edit_client_payment',
    'manage_clients',
    'delete_clients',
    'transfer_client_to_agent',
  ],
  view_client_payment: ['edit_client_payment'],
  view_payments: ['edit_client_payment'],
  
  view_leads: ['create_leads', 'edit_leads', 'assign_leads', 'manage_leads'],
  
  view_tasks: [
    'create_tasks',
    'edit_tasks',
    'delete_tasks',
    'complete_tasks',
    'manage_task_kinds',
  ],
  
  view_manager: [
    'create_manager',
    'edit_manager',
    'delete_manager',
    'change_manager_password',
  ],
  
  view_agent: [
    'create_agent',
    'edit_agent',
    'delete_agent',
    'change_agent_password',
  ],
  
  view_potential_clients: [
    'edit_potential_clients',
    'delete_potential_clients',
    'convert_potential_clients',
  ],
  
  view_slots: [
    'create_slots',
    'edit_slots',
  ],
  
  view_proposals: [
    'create_proposals',
    'edit_proposals',
  ],
  
  view_vendors: ['edit_vendors'],
  
  // Security & Sessions
  view_sessions: ['view_failed_logins', 'unlock_accounts'],
};

/**
 * Apply dependencies when toggling a permission
 * When enabled: adds all required dependencies
 * When disabled: removes the permission and all its children
 * 
 * @param current - Current array of permissions
 * @param toggled - Permission being toggled
 * @param enabled - Whether the permission is being enabled (true) or disabled (false)
 * @returns Updated array of permissions
 */
export function applyDependenciesOnToggle(
  current: string[],
  toggled: string,
  enabled: boolean
): string[] {
  const set = new Set(current);

  if (enabled) {
    // Add the permission
    set.add(toggled);

    // Add all dependencies recursively
    const stack = [toggled];
    while (stack.length > 0) {
      const p = stack.pop()!;
      const deps = PERMISSION_DEPENDENCIES[p] || [];
      for (const dep of deps) {
        if (!set.has(dep)) {
          set.add(dep);
          stack.push(dep);
        }
      }
    }
  } else {
    // For OFF: remove perm and its children recursively
    const removeStack = [toggled];
    while (removeStack.length > 0) {
      const p = removeStack.pop()!;
      if (!set.has(p)) continue;
      set.delete(p);
      const children = PERMISSION_CHILDREN[p] || [];
      for (const child of children) {
        if (set.has(child)) {
          removeStack.push(child);
        }
      }
    }
  }

  return Array.from(set).sort();
}

/**
 * Check if a user has all permissions (Full Access like Admin)
 * 
 * @param userPermissions - Array of user's permissions
 * @returns True if user has all permissions
 * 
 * NOTE: Having all permissions does NOT make a manager an admin.
 * This function is only used for UI display purposes (e.g., showing "Full Access" badge).
 * For access control, always use role-based checks (isAdminRole, isManagerRole).
 */
export function hasFullPermissions(userPermissions: string[] = []): boolean {
  if (!Array.isArray(userPermissions)) return false;
  if (userPermissions.length < ALL_PERMISSIONS.length) return false;

  const set = new Set(userPermissions);
  return ALL_PERMISSIONS.every(p => set.has(p));
}

/**
 * Role helper functions for proper access control
 * These ensure managers are NEVER treated as admins, even with full permissions
 */

export type User = {
  role?: string;
  email?: string;
  permissions?: string[];
};

/**
 * Check if user is an admin role (admin or superadmin)
 * Managers are NEVER considered admins, even with all permissions
 */
export function isAdminRole(user: User | null | undefined): boolean {
  if (!user) return false;
  return user.role === 'admin' || user.role === 'superadmin';
}

/**
 * Check if user is a manager role
 */
export function isManagerRole(user: User | null | undefined): boolean {
  if (!user) return false;
  return user.role === 'manager';
}

/**
 * Check if user is a vendor role (supplier portal - sees only their company's vendor profile)
 */
export function isVendorRole(user: User | null | undefined): boolean {
  if (!user) return false;
  return user.role === 'vendor';
}

/**
 * Check if user is the main account (vp@wjsinnovations.com)
 * This is a special account that may have extended visibility
 */
export function isMainAccountLevel(user: User | null | undefined): boolean {
  if (!user) return false;
  
  // Check by email (fallback method)
  if (user.email?.toLowerCase() === 'vp@wjsinnovations.com') {
    return true;
  }
  
  // If there's a dedicated role or flag in the future, check it here
  // e.g., if (user.role === 'vp' || user.isMainAccount) return true;
  
  return false;
}

/**
 * Check if user can see admin-only content
 * Only real admins, superadmins, or the main account can see admin content
 * Managers are NEVER allowed, even with full permissions
 */
export function canSeeAdminContent(user: User | null | undefined): boolean {
  if (!user) return false;
  return isAdminRole(user) || isMainAccountLevel(user);
}

/**
 * Permission Types: READ, WRITE, MANAGE, DELETE
 * Used for UI display and categorization
 */
export const PERMISSION_TYPES: Record<string, 'READ' | 'WRITE' | 'MANAGE' | 'DELETE'> = {
  // Clients - READ
  view_clients: 'READ',
  view_client_details: 'READ',
  view_client_agent: 'READ',
  view_client_payment: 'READ',
  view_payments: 'READ',
  
  // Clients - WRITE
  edit_clients: 'WRITE',
  add_clients: 'WRITE',
  renew_clients: 'WRITE',
  change_client_password: 'WRITE',
  edit_client_payment: 'WRITE',
  
  // Clients - DELETE
  delete_clients: 'DELETE',
  
  // Clients - MANAGE
  manage_clients: 'MANAGE',
  transfer_client_to_agent: 'MANAGE',
  
  // Leads - READ
  view_leads: 'READ',
  
  // Leads - WRITE
  create_leads: 'WRITE',
  edit_leads: 'WRITE',
  
  // Leads - MANAGE
  assign_leads: 'MANAGE',
  manage_leads: 'MANAGE',
  
  // Tasks - READ
  view_tasks: 'READ',
  
  // Tasks - WRITE
  create_tasks: 'WRITE',
  edit_tasks: 'WRITE',
  complete_tasks: 'WRITE',
  
  // Tasks - DELETE
  delete_tasks: 'DELETE',
  
  // Tasks - MANAGE
  manage_task_kinds: 'MANAGE',
  
  // Managers - READ
  view_manager: 'READ',
  
  // Managers - WRITE
  create_manager: 'WRITE',
  edit_manager: 'WRITE',
  change_manager_password: 'WRITE',
  
  // Managers - DELETE
  delete_manager: 'DELETE',
  
  // Agents - READ
  view_agent: 'READ',
  
  // Agents - WRITE
  create_agent: 'WRITE',
  edit_agent: 'WRITE',
  change_agent_password: 'WRITE',
  
  // Agents - DELETE
  delete_agent: 'DELETE',
  
  // Potential Clients - READ
  view_potential_clients: 'READ',
  
  // Potential Clients - WRITE
  edit_potential_clients: 'WRITE',
  convert_potential_clients: 'WRITE',
  
  // Potential Clients - DELETE
  delete_potential_clients: 'DELETE',
  
  // System - READ
  view_slots: 'READ',
  overview: 'READ',
  view_sessions: 'READ',
  view_failed_logins: 'READ',
  
  // System - WRITE
  create_slots: 'WRITE',
  edit_slots: 'WRITE',
  
  // System - MANAGE
  manage_orders: 'MANAGE',
  manage_api_keys: 'MANAGE',
  unlock_accounts: 'MANAGE',
  
  // Proposals - READ
  view_proposals: 'READ',
  
  // Proposals - WRITE
  create_proposals: 'WRITE',
  edit_proposals: 'WRITE',
  
  // Vendors - READ
  view_vendors: 'READ',
  // Vendors - WRITE
  edit_vendors: 'WRITE',
};

/**
 * Permission Type Labels (Hebrew)
 * Hebrew translations for permission types
 */
export const PERMISSION_TYPE_LABELS: Record<'READ' | 'WRITE' | 'MANAGE' | 'DELETE', string> = {
  READ: 'קריאה',
  WRITE: 'כתיבה',
  MANAGE: 'ניהול',
  DELETE: 'מחיקה',
};

/**
 * Permission Descriptions (Hebrew tooltips)
 * Short, clear explanations for each permission
 */
export const PERMISSION_DESCRIPTIONS: Record<string, string> = {
  // Clients
  view_clients: 'מאפשר לצפות ברשימת כל הלקוחות במערכת',
  edit_clients: 'מאפשר לערוך מידע על לקוחות קיימים',
  add_clients: 'מאפשר להוסיף לקוחות חדשים למערכת',
  renew_clients: 'מאפשר לחדש מנויים של לקוחות',
  change_client_password: 'מאפשר לשנות את סיסמת הלקוח',
  view_client_details: 'מאפשר לצפות בפרטים המלאים של לקוח',
  view_client_agent: 'מאפשר לצפות במידע על הסוכן/מנהל המטפל בלקוח',
  view_client_payment: 'מאפשר לצפות בתשלומים של לקוח ספציפי',
  edit_client_payment: 'מאפשר לערוך תשלומים של לקוחות',
  view_payments: 'מאפשר לצפות בכל התשלומים במערכת',
  manage_clients: 'מאפשר ניהול מלא של לקוחות כולל פעולות מתקדמות',
  delete_clients: 'מאפשר למחוק לקוחות מהמערכת',
  transfer_client_to_agent: 'מאפשר להעביר לקוח לסוכן ולהחזירו לסטטוס פוטנציאלי',
  
  // Leads
  view_leads: 'מאפשר לצפות ברשימת הלידים (לקוחות פוטנציאליים)',
  create_leads: 'מאפשר ליצור לידים חדשים',
  edit_leads: 'מאפשר לערוך מידע על לידים קיימים',
  assign_leads: 'מאפשר להקצות לידים לעובדים או סוכנים',
  manage_leads: 'מאפשר ניהול מלא של לידים כולל פעולות מתקדמות',
  
  // Tasks
  view_tasks: 'מאפשר לצפות ברשימת המשימות',
  create_tasks: 'מאפשר ליצור משימות חדשות',
  edit_tasks: 'מאפשר לערוך משימות קיימות',
  delete_tasks: 'מאפשר למחוק משימות',
  complete_tasks: 'מאפשר לסמן משימות כהושלמו',
  manage_task_kinds: 'מאפשר לנהל סוגי משימות (Task Kinds)',
  
  // Managers
  view_manager: 'מאפשר לצפות ברשימת המשתמשים/מנהלים',
  create_manager: 'מאפשר ליצור משתמשים/מנהלים חדשים',
  edit_manager: 'מאפשר לערוך מידע על משתמשים/מנהלים',
  delete_manager: 'מאפשר למחוק משתמשים/מנהלים',
  change_manager_password: 'מאפשר לשנות את סיסמת המשתמש/מנהל',
  
  // Agents
  create_agent: 'מאפשר ליצור סוכנים חדשים',
  view_agent: 'מאפשר לצפות ברשימת הסוכנים',
  edit_agent: 'מאפשר לערוך מידע על סוכנים',
  change_agent_password: 'מאפשר לשנות את סיסמת הסוכן',
  delete_agent: 'מאפשר למחוק סוכנים',
  
  // Potential Clients
  view_potential_clients: 'מאפשר לצפות ברשימת הלקוחות הפוטנציאליים',
  edit_potential_clients: 'מאפשר לערוך מידע על לקוחות פוטנציאליים',
  delete_potential_clients: 'מאפשר למחוק לקוחות פוטנציאליים',
  convert_potential_clients: 'מאפשר להמיר ליד ללקוח',
  
  // System
  view_slots: 'מאפשר לצפות בתורים/פגישות',
  create_slots: 'מאפשר ליצור תורים/פגישות חדשים',
  edit_slots: 'מאפשר לערוך תורים/פגישות קיימים',
  overview: 'מאפשר לצפות בנתונים כלליים וסטטיסטיקות',
  manage_orders: 'מאפשר ניהול מלא של הזמנות כולל עריכה והפעלת פעולות',
  manage_api_keys: 'מאפשר יצירה, עריכה וניהול מפתחות API',
  view_sessions: 'מאפשר לצפות בפעילויות וסשנים פעילים של משתמשים',
  view_failed_logins: 'מאפשר לצפות בניסיונות כניסה כושלים',
  unlock_accounts: 'מאפשר לשחרר חשבונות שננעלו לאחר ניסיונות כושלים',
  
  // Proposals
  view_proposals: 'מאפשר לצפות ברשימת הצעות המחיר',
  create_proposals: 'מאפשר ליצור הצעות מחיר חדשות',
  edit_proposals: 'מאפשר לערוך הצעות מחיר קיימות',
  
  // Vendors
  view_vendors: 'מאפשר לצפות ברשימת הספקים ולגשת לפרופילי ספקים',
  edit_vendors: 'מאפשר לערוך ספקים: שמירת שינויים, הוספת אנשי קשר, חוזים, קישורי ישויות והערות',
};

